import { useState, useMemo, useCallback } from 'react';
import { X, Save, Search, ChevronDown, Target, Check, TreePine, Pencil, Eye, PenLine } from 'lucide-react';
import { useTheme } from '../../hooks/fetch_api_hook/useTheme';
import RichTextBlock from './RichTextBlock';
import RichTextBlockEleve from './RichTextBlockEleve';
import QuestionPreviewModal from './QuestionPreviewModal';
import QuestionTypeSelector from '../VueEvalNat/BilanEval/FormBilanEval/FormQuestion/QuestionTypeSelector';
import QCMResponseEditor from '../VueEvalNat/BilanEval/FormBilanEval/FormQuestion/ResponseEditors/QCMResponseEditor';
import TexteLibreResponseEditor from '../VueEvalNat/BilanEval/FormBilanEval/FormQuestion/ResponseEditors/TexteLibreResponseEditor';
import AssociationResponseEditor from '../VueEvalNat/BilanEval/FormBilanEval/FormQuestion/ResponseEditors/AssociationResponseEditor';
import GeogebraChoixModal, { useGeogebraModalState } from '../VueEvalNat/BilanEval/FormBilanEval/FormQuestion/Elements/GeogebraChoixModal';
import { buildQuestionPayload } from '../../utils/questionUtils';
import MathText from './MathText';
import { partitionnerParRubrique } from '../../utils/rubriqueAttendus';
import SeparateurRubrique from '../general/SeparateurRubrique';

const LETTRES = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
const DEFAULT_RESPONSE_COUNT = 4;

const COMPS_MATH = [
  { id: 1, nom: "Calculer" },
  { id: 2, nom: "Chercher" },
  { id: 3, nom: "Communiquer" },
  { id: 4, nom: "Raisonner" },
  { id: 5, nom: "Modéliser" },
  { id: 6, nom: "Représenter" },
];
const COMPS_FRA = [
  { id: 7, nom: "Comprendre et s'exprimer à l'oral" },
  { id: 8, nom: "Lire" },
  { id: 9, nom: "Écrire" },
  { id: 10, nom: "Comprendre le fonctionnement de la langue" },
  { id: 11, nom: "Acquérir des éléments de culture littéraire et artistique" },
];


// mode: 'text' | 'element'
const emptyResponse = () => ({ mode: 'text', contenu: '', element: null });
const makeEmptyResponses = (count = DEFAULT_RESPONSE_COUNT) => Array.from({ length: count }, emptyResponse);


const normalize = (s) =>
  (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

// Convertit une config tableau en format [ligne|colonne]
const configToComplexAnswer = (config) => {
  if (!config?.lignes || !config?.colonnes || !config?.bonnesReponses) return '';
  return config.lignes.map((ligne, idx) => {
    const colonne = config.colonnes[config.bonnesReponses[idx]] || config.colonnes[0];
    return `[${ligne}|${colonne}]`;
  }).join('');
};

// Construit le contenu pour les types association
const buildAssociationContenu = (subType, data) => {
  if (subType === 'relier') {
    const pairs = data.pairs || [];
    // cibles_leurres : recopié s'il existe (sinon un exercice généré avec leurres
    // serait silencieusement amputé à chaque ré-enregistrement par le prof)
    const leurres = data.cibles_leurres || [];
    return {
      subType: 'relier', pairs,
      ...(leurres.length ? { cibles_leurres: leurres } : {}),
      bonne_reponse: JSON.stringify(pairs.map(p => [p.source, p.target]).sort((a, b) => a[0].localeCompare(b[0]))),
    };
  }
  if (subType === 'ordonner') {
    const items = data.items || [];
    return { subType: 'ordonner', items, bonne_reponse: JSON.stringify(items) };
  }
  if (subType === 'texte_trou') {
    return { subType: 'texte_trou', phrase: data.phrase || '', mode: data.mode || 'drag', distracteurs: data.distracteurs || [], strict: data.strict || false, bonne_reponse: data.phrase || '' };
  }
  if (subType === 'classer') {
    const categories = data.categories || [];
    const sorted = {};
    categories.forEach(c => { sorted[c.name] = [...(c.items || [])].sort(); });
    return { subType: 'classer', categories, bonne_reponse: JSON.stringify(sorted) };
  }
  return {};
};

const QuestionForm = ({ question = null, onSubmit, onCancel, niveau = '', matiere = '', niveauChar = '', descripteurContext = null, draftMode = false, attenduConnu = null }) => {
  const isEditing = Boolean(question);

  // Normalise elements_additionnels (peut être un string JSON depuis l'API)
  const parsedElements = (() => {
    const ea = question?.elements_additionnels;
    if (!ea) return [];
    if (Array.isArray(ea?.elements)) return ea.elements;
    if (typeof ea === 'string') {
      try { return JSON.parse(ea)?.elements || []; } catch { return []; }
    }
    return ea?.elements || [];
  })();

  // ─── Énoncé state (texte enrichi + éléments intégrés, sans le tableau qui est géré séparément) ───
  const [enonce, setEnonce] = useState(() => ({
    texte: question?.texte || '',
    elements: parsedElements.filter(e => e.type !== 'table'),
  }));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPreviewEnonce, setShowPreviewEnonce] = useState(false);
  const [showPreviewReponses, setShowPreviewReponses] = useState(false);
  const [showFinalPreview, setShowFinalPreview] = useState(false);

  // ─── GeoGebra modal pour choix QCM ───
  const [ggbModalIndex, setGgbModalIndex] = useGeogebraModalState();

  // ─── Type de question ───
  const [questionType, setQuestionType] = useState(() => {
    if (question?.type === 'texte' || question?.type === 'association') return question.type;
    return 'qcm';
  });

  // ─── Texte libre ───
  const [texteAnswer, setTexteAnswer] = useState(() => {
    if (question?.type === 'texte') return question.contenu?.bonne_reponse || '';
    return '';
  });
  const [texteStrict, setTexteStrict] = useState(() => {
    if (question?.type === 'texte') return question.contenu?.strict || false;
    return false;
  });
  const [texteSuffixe, setTexteSuffixe] = useState(() => {
    if (question?.type === 'texte') return question.contenu?.suffixe || '';
    return '';
  });

  // ─── Association ───
  const [associationSubType, setAssociationSubType] = useState(() => {
    if (question?.type === 'association') return question.contenu?.subType || 'relier';
    return 'relier';
  });
  const [associationData, setAssociationData] = useState(() => {
    if (question?.type === 'association' && question.contenu) return question.contenu;
    return {};
  });

  // ─── Tableau (détecté via parsedElements) ───
  const [tableElement, setTableElement] = useState(() => {
    const tbl = parsedElements.find(e => e.type === 'table');
    if (tbl) return { type: 'table', id: Date.now(), config: tbl.config };
    // Fallback : détecter via bonne_reponse format [ligne|colonne]
    if (question?.contenu?.bonne_reponse?.includes('[') && question?.contenu?.bonne_reponse?.includes('|')) {
      return { type: 'table', id: Date.now(), config: {} };
    }
    return null;
  });

  const isTableMode = Boolean(tableElement);

  // ─── Validation unifiée pour les nouveaux types ───
  const [responseValid, setResponseValid] = useState(false);

  const handleAssociationDataChange = useCallback((newData) => {
    setAssociationData(newData);
  }, []);

  // Texte brut (sans markers ni HTML) pour la validation
  const questionText = useMemo(() => {
    const plain = enonce.texte
      .replace(/\{\{element:\d+\}\}/g, '')
      .replace(/<[^>]*>/g, '')
      .trim();
    return plain;
  }, [enonce.texte]);

  const handleEnonceChange = useCallback(({ texte, elements }) => {
    setEnonce({ texte, elements });
  }, []);

  // Attendu picker state
  const [selectedAttendu, setSelectedAttendu] = useState(
    question?.attendu ? { id: question.attendu, description: question.attendu_titre || '' } : null
  );
  const [attenduSearch, setAttenduSearch] = useState('');
  const [expandedTheme, setExpandedTheme] = useState('');
  const [expandedDesc, setExpandedDesc] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const [pickerMode, setPickerMode] = useState('search'); // 'search' | 'tree'
  const [useFullPicker, setUseFullPicker] = useState(false); // pour basculer hors du contexte descripteur

  // Compétence (optionnelle)
  const [selectedCompetence, setSelectedCompetence] = useState(question?.competence || null);
  const competences = matiere === 'Français' ? COMPS_FRA : COMPS_MATH;

  const initialResponses = useMemo(() => {
    if (question?.contenu?.choix) {
      const mapped = question.contenu.choix.map((c) => {
        if (typeof c === 'string') {
          return { mode: 'text', contenu: c, element: null };
        }
        // Objet élément { type, config }
        return { mode: 'element', contenu: '', element: c };
      });
      // Padder au minimum DEFAULT_RESPONSE_COUNT (4) slots
      while (mapped.length < DEFAULT_RESPONSE_COUNT) mapped.push(emptyResponse());
      return mapped;
    }
    return makeEmptyResponses();
  }, [question]);

  const [responses, setResponses] = useState(initialResponses);

  const initialCorrectIndex = useMemo(() => {
    if (question?.contenu?.bonne_reponse) {
      return LETTRES.indexOf(question.contenu.bonne_reponse);
    }
    return 0;
  }, [question]);

  // Bonnes réponses QCM — Set unifié (simple = 1 élément, multi = plusieurs)
  const [correctAnswerIndexes, setCorrectAnswerIndexes] = useState(() => {
      if (!question?.contenu?.bonne_reponse) return new Set([initialCorrectIndex].filter(i => i >= 0));
      const br = question.contenu.bonne_reponse;
      if (Array.isArray(br)) return new Set(br.map(l => LETTRES.indexOf(l)).filter(i => i >= 0));
      const idx = LETTRES.indexOf(br);
      return idx >= 0 ? new Set([idx]) : new Set([initialCorrectIndex].filter(i => i >= 0));
  });

  // --- Attendu data ---
  const { data: themes } = useTheme(niveauChar);

  const allAttendus = useMemo(() => {
    if (!Array.isArray(themes)) return [];
    const result = [];
    themes.forEach((theme) => {
      (theme.descripteur || []).forEach((desc) => {
        (desc.attendus || []).forEach((att) => {
          result.push({ ...att, themeNom: theme.nom, descripteurNom: desc.nom });
        });
      });
    });
    return result;
  }, [themes]);

  const searchResults = useMemo(() => {
    if (!attenduSearch.trim()) return null;
    const q = normalize(attenduSearch);
    return allAttendus.filter((a) => normalize(a.description).includes(q));
  }, [allAttendus, attenduSearch]);

  // Grouper les résultats par thème > descripteur pour affichage style arbre
  const groupedSearchResults = useMemo(() => {
    if (!searchResults || searchResults.length === 0) return null;
    const groups = {};
    searchResults.forEach((att) => {
      const key = att.themeNom || 'Autre';
      if (!groups[key]) groups[key] = {};
      const descKey = att.descripteurNom || 'Autre';
      if (!groups[key][descKey]) groups[key][descKey] = [];
      groups[key][descKey].push(att);
    });
    return groups;
  }, [searchResults]);

  // Resolution en mode edit (on a l'id mais pas la description)
  const resolvedAttendu = useMemo(() => {
    if (selectedAttendu?.description) return selectedAttendu;
    if (question?.attendu && allAttendus.length > 0) {
      const found = allAttendus.find((a) => a.id === question.attendu);
      if (found) return found;
    }
    // Attendu d'un autre niveau que la classe (2026-09-29) : absent de l'arbre
    // de la classe, son libellé vient de la page qui ouvre le formulaire
    // (carte d'attendu du composeur, exerciseur) ; sinon la puce restait vide.
    if (selectedAttendu) {
      const connu = [
        attenduConnu,
        ...(descripteurContext?.attendus || []).map((a) => ({
          ...a, themeNom: descripteurContext.themeName, descripteurNom: descripteurContext.descName,
        })),
      ].find((a) => a && a.id === selectedAttendu.id && a.description);
      if (connu) return connu;
    }
    return selectedAttendu;
  }, [selectedAttendu, question, allAttendus, attenduConnu, descripteurContext]);

  const handleSelectAttendu = (attendu) => {
    setSelectedAttendu(resolvedAttendu?.id === attendu.id ? null : attendu);
    setShowPicker(false);
    setAttenduSearch('');
  };

  const handleClearAttendu = () => setSelectedAttendu(null);

  // ─── Validation adaptée au type ───
  const hasAttendu = Boolean(resolvedAttendu);
  const hasEnonce = questionText.trim().length > 0;

  // QCM classique : 4 choix remplis + bonne réponse sélectionnée
  // Autres types : piloté par responseValid (via onValidChange des éditeurs)
  // Tous les types : la validation est gérée par l'éditeur via onValidChange → responseValid
  const canSubmit = hasEnonce && responseValid;

  const steps = [
    { num: '1', label: 'Attendu', done: hasAttendu },
    { num: '2', label: 'Énoncé', done: hasEnonce },
    { num: '3', label: questionType === 'texte' ? 'Réponse' : 'Réponses', done: responseValid },
  ];

  const missingItems = [
    !hasEnonce && 'énoncé',
    !responseValid && (questionType === 'texte' ? 'réponse' : 'réponses'),
  ].filter(Boolean);

  // Construit le contenu QCM (simple ou multi) — réutilisé dans submit + preview
  const buildQCMContenu = (choix) => {
      const bonnes = [...correctAnswerIndexes].sort();
      return {
          choix,
          bonne_reponse: bonnes.length > 1 ? bonnes.map(idx => LETTRES[idx]) : LETTRES[bonnes[0]],
          ...(bonnes.length > 1 && { multi: true }),
      };
  };

  const isFullyReady = canSubmit;
  const handleSubmit = async () => {
    if (!canSubmit || isSubmitting) return;

    setIsSubmitting(true);

    try {
      // ─── 1. Construire le contenu selon le type de question (données brutes, pas d'upload) ───
      let contenu;
      const allElements = [...enonce.elements];

      if (isTableMode) {
        // Tableau (vrai/faux, colonnes) → stocké comme type 'qcm' pour rétro-compat
        // bonne_reponse au format [ligne|colonne] ex: "[chat|Vrai][chien|Faux]"
        allElements.push(tableElement);
        contenu = { choix: [], bonne_reponse: configToComplexAnswer(tableElement.config) };

      } else if (questionType === 'qcm') {
        // QCM : extraire les choix depuis le state responses (texte ou éléments riches)
        const choix = responses.map(r => r.mode === 'element' && r.element ? r.element : r.contenu);
        contenu = buildQCMContenu(choix);

      } else if (questionType === 'texte') {
        contenu = { bonne_reponse: texteAnswer, strict: texteStrict, suffixe: texteSuffixe };

      } else if (questionType === 'association') {
        contenu = buildAssociationContenu(associationSubType, associationData);
      }

      // ─── 2. Assemblage + upload R2 via buildQuestionPayload ───
      // skipUpload=true en draftMode (édition locale, pas d'envoi en BDD)
      // skipUpload=false en mode normal (upload images R2 avant envoi)
      const raw = {
        texte: enonce.texte,
        type: isTableMode ? 'qcm' : questionType,
        contenu,
        elements_additionnels: allElements.length > 0 ? { version: 1, elements: allElements } : null,
        niveau,
        matiere,
        attendu: resolvedAttendu?.id || null,
        competence: selectedCompetence || null,
      };

      const data = await buildQuestionPayload(raw, { skipUpload: draftMode });
      await onSubmit(data);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-base sm:text-xl font-bold text-gray-800">
          {isEditing ? 'Modifier la question' : 'Nouvelle question'}
        </h2>
        <button
          onClick={onCancel}
          aria-label="Annuler"
          className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition"
        >
          <X size={20} />
        </button>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-0">
        {steps.map((step, i) => (
          <div key={step.num} className="flex items-center flex-1">
            <div className="flex items-center gap-1.5">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-extrabold transition-all duration-300 ${
                step.done
                  ? 'bg-emerald-500 text-white shadow-sm'
                  : 'bg-gray-200 text-gray-400'
              }`}>
                {step.done ? <Check size={12} strokeWidth={3} /> : step.num}
              </div>
              <span className={`hidden sm:inline text-xs font-semibold transition-colors duration-300 ${
                step.done ? 'text-emerald-600' : 'text-gray-400'
              }`}>{step.label}</span>
            </div>
            {i < steps.length - 1 && (
              <div className={`flex-1 h-px mx-2 transition-colors duration-300 ${
                step.done ? 'bg-emerald-300' : 'bg-gray-200'
              }`} />
            )}
          </div>
        ))}
      </div>

      {/* Attendu du referentiel (optionnel) */}
      <div className="p-3 sm:p-5 rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide flex items-center gap-2">
            <span className="w-5 h-5 rounded bg-sky-100 text-sky-800 text-[10px] font-extrabold flex items-center justify-center">1</span>
            Attendu du référentiel
          </h3>
          <span className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Optionnel</span>
        </div>

        {/* Attendu sélectionné. Ouvert depuis une carte d'attendu
            (descripteurContext) : l'exercice appartient à CET attendu, le
            rattachement est FIGÉ — affichage seul, pas de crayon (décision
            2026-08-18). Hors contexte (import HTML, création libre) : chip
            cliquable pour changer. */}
        {resolvedAttendu ? (
          descripteurContext ? (
            <div className="w-full flex items-center gap-2 p-2.5 rounded-lg bg-cyan-50/60 border border-cyan-200 text-left">
              {resolvedAttendu.themeNom && (
                <span className="text-[10px] font-semibold text-cyan-700 bg-cyan-100 px-2 py-0.5 rounded flex-shrink-0">
                  {resolvedAttendu.themeNom}
                </span>
              )}
              {resolvedAttendu.descripteurNom && (
                <>
                  <span className="text-gray-300 text-xs">›</span>
                  <span className="text-[10px] text-gray-500">{resolvedAttendu.descripteurNom}</span>
                  <span className="text-gray-300 text-xs">›</span>
                </>
              )}
              <span className="text-xs text-cyan-800 font-semibold flex-1 truncate">
                <MathText>{resolvedAttendu.description}</MathText>
              </span>
            </div>
          ) : (
          <button
            type="button"
            onClick={() => { handleClearAttendu(); setShowPicker(true); }}
            className="w-full flex items-center gap-2 p-2.5 rounded-lg bg-cyan-50/60 border border-cyan-200 text-left group hover:bg-cyan-50 transition"
          >
            {resolvedAttendu.themeNom && (
              <span className="text-[10px] font-semibold text-cyan-700 bg-cyan-100 px-2 py-0.5 rounded flex-shrink-0">
                {resolvedAttendu.themeNom}
              </span>
            )}
            {resolvedAttendu.descripteurNom && (
              <>
                <span className="text-gray-300 text-xs">›</span>
                <span className="text-[10px] text-gray-500">{resolvedAttendu.descripteurNom}</span>
                <span className="text-gray-300 text-xs">›</span>
              </>
            )}
            <span className="text-xs text-cyan-800 font-semibold flex-1 truncate">
              <MathText>{resolvedAttendu.description}</MathText>
            </span>
            <Pencil size={12} className="text-gray-400 group-hover:text-gray-600 flex-shrink-0" />
          </button>
          )
        ) : descripteurContext && !useFullPicker ? (
          /* Mode contexte descripteur : liste simple des attendus du descripteur */
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-semibold text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded">
                {descripteurContext.themeName}
              </span>
              <span className="text-gray-300 text-xs">›</span>
              <span className="text-[10px] font-medium text-blue-600">
                {descripteurContext.descName}
              </span>
            </div>
            <div className="space-y-1 rounded-lg border border-blue-100 bg-blue-50/30 p-2">
              {(() => {
                const renderAtt = (att) => (
                  <button
                    key={att.id}
                    type="button"
                    onClick={() => handleSelectAttendu(att)}
                    className="w-full text-left px-3 py-2 rounded-lg text-sm leading-snug transition-all flex items-center gap-2 text-gray-600 hover:bg-emerald-50 hover:text-emerald-700 hover:shadow-sm"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 flex-shrink-0" />
                    <MathText>{att.description}</MathText>
                  </button>
                );
                const { objectifs, automatismes } = partitionnerParRubrique(descripteurContext.attendus);
                if (automatismes.length === 0) return descripteurContext.attendus.map(renderAtt);
                return (
                  <>
                    {objectifs.length > 0 ? <SeparateurRubrique rubrique="objectif" className="px-2" count={objectifs.length} /> : null}
                    {objectifs.map(renderAtt)}
                    <SeparateurRubrique rubrique="automatisme" className="px-2" count={automatismes.length} />
                    {automatismes.map(renderAtt)}
                  </>
                );
              })()}
            </div>
            <button
              type="button"
              onClick={() => setUseFullPicker(true)}
              className="mt-2 text-[11px] text-gray-400 hover:text-indigo-500 transition flex items-center gap-1"
            >
              <TreePine size={11} />
              Parcourir tout le référentiel
            </button>
          </div>
        ) : (
          /* Champ recherche + toggle arbre */
          <div className="relative">
            <div className="flex gap-0">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  value={attenduSearch}
                  onChange={(e) => { setAttenduSearch(e.target.value); setPickerMode('search'); setShowPicker(true); }}
                  onFocus={() => setShowPicker(true)}
                  placeholder="Rechercher un attendu... (ex: fraction, symétrie)"
                  className="w-full pl-9 pr-3 py-2.5 rounded-l-lg border border-gray-200 text-sm text-gray-700 placeholder-gray-400 focus:ring-2 focus:ring-cyan-200/50 focus:border-cyan-300 focus:outline-none"
                />
              </div>
              <button
                type="button"
                onClick={() => { setPickerMode(pickerMode === 'tree' ? 'search' : 'tree'); setShowPicker(true); }}
                title="Parcourir l'arbre"
                className={`px-3 border border-l-0 rounded-r-lg transition ${
                  pickerMode === 'tree'
                    ? 'bg-emerald-50 border-emerald-400 text-emerald-600'
                    : 'bg-white border-gray-200 text-emerald-400 hover:text-emerald-600 hover:bg-emerald-50/50'
                }`}
              >
                <TreePine size={16} />
              </button>
            </div>

            {/* Dropdown unifié */}
            {showPicker && (
              <>
                {/* Overlay pour fermer */}
                <div className="fixed inset-0 z-40" onClick={() => { setShowPicker(false); setAttenduSearch(''); }} />

                <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden flex flex-col" style={{ maxHeight: '22rem' }}>
                  <div className="overflow-y-auto flex-1 p-1.5">

                    {/* MODE RECHERCHE */}
                    {pickerMode === 'search' && (
                      <>
                        {attenduSearch.trim().length >= 2 && searchResults ? (
                          groupedSearchResults ? (
                            <div className="space-y-0.5">
                              {Object.entries(groupedSearchResults).map(([themeName, descs]) => (
                                <div key={themeName}>
                                  {/* Thème */}
                                  <div className="px-3 py-1.5 text-sm font-semibold text-indigo-700 bg-indigo-50/60 rounded-lg">
                                    {themeName}
                                  </div>
                                  <div className="ml-4 pl-3 border-l-2 border-indigo-200 space-y-0.5 pb-1">
                                    {Object.entries(descs).map(([descName, atts]) => (
                                      <div key={descName}>
                                        {/* Descripteur */}
                                        <div className="px-2.5 py-1 text-sm font-semibold text-blue-700 flex items-center gap-2">
                                          <span className="w-1 h-1 rounded-full bg-blue-400 flex-shrink-0" />
                                          {descName}
                                          <span className="text-[10px] text-gray-400">{atts.length}</span>
                                        </div>
                                        {/* Attendus */}
                                        <div className="ml-3 pl-3 border-l-2 border-blue-100 space-y-0.5 py-0.5">
                                          {atts.map((att) => (
                                            <button
                                              key={att.id}
                                              type="button"
                                              onClick={() => handleSelectAttendu(att)}
                                              className="w-full text-left px-3 py-1.5 rounded-md text-sm leading-snug transition-all flex items-center gap-2 text-gray-600 hover:bg-emerald-50 hover:text-emerald-700"
                                            >
                                              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 flex-shrink-0" />
                                              <MathText>{att.description}</MathText>
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-400 text-center py-6">
                              Aucun attendu pour « {attenduSearch} »
                            </p>
                          )
                        ) : (
                          <p className="text-xs text-gray-400 text-center py-6">
                            Tape 2 lettres pour chercher ou clique <TreePine size={12} className="inline text-emerald-400" /> pour parcourir
                          </p>
                        )}
                      </>
                    )}

                    {/* MODE ARBRE */}
                    {pickerMode === 'tree' && (
                      Array.isArray(themes) && themes.length > 0 ? (
                        <div className="space-y-0.5">
                          {themes.map((theme) => {
                            const isThemeOpen = expandedTheme === theme.nom;
                            return (
                              <div key={theme.id}>
                                <button
                                  type="button"
                                  onClick={() => { setExpandedTheme(isThemeOpen ? '' : theme.nom); setExpandedDesc(''); }}
                                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                                    isThemeOpen ? 'text-indigo-700 bg-indigo-50/60' : 'text-gray-700 hover:bg-gray-50'
                                  }`}
                                >
                                  <ChevronDown size={13} className={`text-gray-400 transition-transform duration-200 flex-shrink-0 ${isThemeOpen ? 'rotate-0' : '-rotate-90'}`} />
                                  <span className="flex-1 text-left">{theme.nom}</span>
                                </button>

                                {isThemeOpen && (
                                  <div className="ml-4 pl-3 border-l-2 border-indigo-200 space-y-0.5 pb-1">
                                    {(theme.descripteur || []).map((desc) => {
                                      const isDescOpen = expandedDesc === desc.nom;
                                      return (
                                        <div key={desc.id}>
                                          <button
                                            type="button"
                                            onClick={() => setExpandedDesc(isDescOpen ? '' : desc.nom)}
                                            className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-sm transition-colors ${
                                              isDescOpen ? 'text-blue-700 bg-blue-50/70 font-semibold' : 'text-gray-600 hover:bg-gray-50 font-medium'
                                            }`}
                                          >
                                            <ChevronDown size={12} className={`text-gray-400 transition-transform duration-200 flex-shrink-0 ${isDescOpen ? 'rotate-0' : '-rotate-90'}`} />
                                            <span className="flex-1 text-left">{desc.nom}</span>
                                            <span className="text-[10px] text-gray-400">{(desc.attendus || []).length}</span>
                                          </button>

                                          {isDescOpen && (desc.attendus || []).length > 0 && (
                                            <div className="ml-3 pl-3 border-l-2 border-blue-100 space-y-0.5 py-0.5">
                                              {(() => {
                                                const renderAtt = (att) => {
                                                  const isSelected = resolvedAttendu?.id === att.id;
                                                  return (
                                                    <button
                                                      key={att.id}
                                                      type="button"
                                                      onClick={() => handleSelectAttendu({ ...att, themeNom: theme.nom, descripteurNom: desc.nom })}
                                                      className={`w-full text-left px-3 py-1.5 rounded-md text-sm leading-snug transition-all flex items-center gap-2 ${
                                                        isSelected
                                                          ? 'bg-emerald-100 text-emerald-800 font-medium ring-1 ring-emerald-300'
                                                          : 'text-gray-600 hover:bg-emerald-50 hover:text-emerald-700'
                                                      }`}
                                                    >
                                                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isSelected ? 'bg-emerald-500' : 'bg-cyan-400'}`} />
                                                      <MathText>{att.description}</MathText>
                                                    </button>
                                                  );
                                                };
                                                const { objectifs, automatismes } = partitionnerParRubrique(desc.attendus);
                                                if (automatismes.length === 0) return desc.attendus.map(renderAtt);
                                                return (
                                                  <>
                                                    {objectifs.length > 0 ? <SeparateurRubrique rubrique="objectif" className="px-3" count={objectifs.length} /> : null}
                                                    {objectifs.map(renderAtt)}
                                                    <SeparateurRubrique rubrique="automatisme" className="px-3" count={automatismes.length} />
                                                    {automatismes.map(renderAtt)}
                                                  </>
                                                );
                                              })()}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400 text-center py-6">
                          Aucun référentiel disponible
                        </p>
                      )
                    )}
                  </div>

                  {/* Footer fermer */}
                  <div className="px-3 py-2 border-t border-gray-100 flex justify-end">
                    <button
                      type="button"
                      onClick={() => { setShowPicker(false); setAttenduSearch(''); }}
                      className="text-[10px] text-gray-400 hover:text-gray-600 px-2 py-1 rounded transition"
                    >
                      Fermer
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
        {/* Compétence évaluée — visible seulement quand attendu sélectionné */}
        {resolvedAttendu && (
          <div className="mt-3 flex items-center gap-2">
            <Target size={13} className="text-gray-400 flex-shrink-0" />
            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide flex-shrink-0">Compétence</span>
            <div className="flex flex-wrap gap-1.5 flex-1">
              {competences.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setSelectedCompetence(selectedCompetence === c.id ? null : c.id)}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all ${
                    selectedCompetence === c.id
                      ? 'bg-indigo-100 text-indigo-700 ring-1 ring-indigo-300'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                >
                  {c.nom}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Énoncé — Éditeur enrichi unifié */}
      <div className="p-3 sm:p-5 rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide flex items-center gap-2">
            <span className="w-5 h-5 rounded bg-sky-100 text-sky-800 text-[10px] font-extrabold flex items-center justify-center">2</span>
            Énoncé
          </h3>
          <button
            type="button"
            onClick={() => setShowPreviewEnonce(p => !p)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              showPreviewEnonce
                ? 'bg-indigo-50 text-indigo-600 ring-1 ring-indigo-200'
                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
            }`}
          >
            {showPreviewEnonce ? <PenLine size={13} /> : <Eye size={13} />}
            {showPreviewEnonce ? 'Éditer' : 'Aperçu'}
          </button>
        </div>

        {showPreviewEnonce ? (
          <div className="rounded-xl bg-slate-800 p-3 sm:p-5 min-h-[80px] sm:min-h-[100px]">
            {hasEnonce ? (
              <RichTextBlockEleve texte={enonce.texte} elements={enonce.elements} />
            ) : (
              <p className="text-slate-500 text-sm italic">Aucun énoncé rédigé</p>
            )}
          </div>
        ) : (
          <RichTextBlock
            texte={enonce.texte}
            elements={enonce.elements}
            onChange={handleEnonceChange}
          />
        )}
      </div>

      {/* Sélecteur de type */}
      <QuestionTypeSelector value={questionType} onChange={setQuestionType} />

      {/* ═══ RÉPONSES — selon le type ═══ */}

      {/* Réponses QCM classique — réutilise QCMResponseEditor */}
      {questionType === 'qcm' && !isTableMode && (
        <QCMResponseEditor
          responses={responses}
          setResponses={setResponses}
          correctAnswerIndexes={correctAnswerIndexes}
          setCorrectAnswerIndexes={setCorrectAnswerIndexes}
          showPreview={showPreviewReponses}
          onTogglePreview={() => setShowPreviewReponses(p => !p)}
          onValidChange={setResponseValid}
          onOpenGeogebra={setGgbModalIndex}
        />
      )}

      {/* Texte libre */}
      {questionType === 'texte' && (
        <TexteLibreResponseEditor
          answer={texteAnswer}
          onAnswerChange={setTexteAnswer}
          strict={texteStrict}
          onStrictChange={setTexteStrict}
          suffixe={texteSuffixe}
          onSuffixeChange={setTexteSuffixe}
          onValidChange={setResponseValid}
        />
      )}

      {/* Association (relier, ordonner, texte à trous, classer, tableau) */}
      {(questionType === 'association' || isTableMode) && (
        <AssociationResponseEditor
          subType={isTableMode ? 'tableau' : associationSubType}
          onSubTypeChange={setAssociationSubType}
          data={associationData}
          onDataChange={handleAssociationDataChange}
          onValidChange={setResponseValid}
          tableElement={tableElement}
          onTableElementChange={setTableElement}
        />
      )}

      {/* Aperçu global */}
      {showFinalPreview && (
        <QuestionPreviewModal
          question={{
            texte: enonce.texte,
            type_question: isTableMode ? 'qcm' : questionType,
            elements_additionnels: (() => {
              const els = [...enonce.elements.map(el => ({ type: el.type, config: el.config || {} }))];
              if (isTableMode && tableElement) els.push(tableElement);
              return els.length > 0 ? { elements: els } : null;
            })(),
            contenu:
              isTableMode ? { choix: [], bonne_reponse: configToComplexAnswer(tableElement?.config) }
              : questionType === 'qcm' ? buildQCMContenu(
                  responses.map(r => r.mode === 'element' && r.element ? r.element : r.contenu)
              )
              : questionType === 'texte' ? { bonne_reponse: texteAnswer, strict: texteStrict, suffixe: texteSuffixe }
              : questionType === 'association' ? buildAssociationContenu(associationSubType, associationData)
              : {},
          }}
          onClose={() => setShowFinalPreview(false)}
        />
      )}

      {/* Modal GeoGebra pour choix QCM */}
      <GeogebraChoixModal
        index={ggbModalIndex}
        responses={responses}
        setResponses={setResponses}
        onClose={() => setGgbModalIndex(null)}
      />

      {/* Boutons d'action */}
      <div className="space-y-2 pt-2">
        {/* Feedback dynamique */}
        <span className="text-xs text-gray-400 block">
          {isFullyReady ? (
            <span className="text-emerald-600 font-semibold flex items-center gap-1">
              <Check size={14} strokeWidth={3} />
              Prêt à enregistrer
            </span>
          ) : missingItems.length > 0 ? (
            <span className="text-orange-500 font-medium">{missingItems.join(', ')} manquant{missingItems.length > 1 ? 's' : ''}</span>
          ) : null}
        </span>

        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <button
            onClick={onCancel}
            aria-label="Annuler"
            className="px-3 sm:px-5 py-2 sm:py-2.5 rounded-xl font-semibold text-xs sm:text-sm border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 transition"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={() => setShowFinalPreview(true)}
            disabled={!hasEnonce}
            className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl font-semibold text-xs sm:text-sm transition ${
              hasEnonce
                ? 'bg-indigo-50 text-indigo-600 border border-indigo-200 hover:bg-indigo-100'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            <Eye size={14} className="sm:w-4 sm:h-4" />
            Aperçu
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || isSubmitting}
            aria-label="Enregistrer"
            className={`flex items-center gap-1.5 sm:gap-2 px-4 sm:px-6 py-2 sm:py-2.5 rounded-xl font-semibold text-xs sm:text-sm transition ml-auto ${
              canSubmit && !isSubmitting
                ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-md shadow-emerald-500/20'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            <Save size={14} className="sm:w-4 sm:h-4" />
            {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuestionForm;
